import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { MoreHorizontal, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Binder "More" overflow dropdown (desktop).
 *
 * Rendered via a portal with fixed positioning so the menu escapes the nav
 * scroll container / stacking context. Supports click, keyboard (Escape),
 * outside-click and route-change dismissal.
 *
 * `vertical` renders the trigger as a folder-divider tab (`.fl-vtab`) in the
 * left vertical binder and opens the menu to the right of the trigger.
 */
export default function BinderMoreDropdown({
  items,
  activePath,
  accent = "#f47a20",
  accentFor,
  testidPrefix = "nav",
  vertical = false,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0, left: 0 });

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (vertical) {
      setPos({ top: Math.max(8, Math.round(r.top)), left: Math.round(r.right + 10), right: 0 });
    } else {
      setPos({ top: Math.round(r.bottom + 6), right: Math.max(8, Math.round(window.innerWidth - r.right)), left: 0 });
    }
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => place();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  useEffect(() => { setOpen(false); }, [activePath]);

  if (!items.length) return null;
  const isActive = items.some((it) => it.to.split("?")[0] === activePath);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testidPrefix}-more-tab`}
        data-active={isActive ? "true" : "false"}
        aria-expanded={open}
        aria-haspopup="menu"
        className={vertical ? "fl-vtab" : "fl-binder-tab"}
        style={{ "--fl-section": accent }}
      >
        <MoreHorizontal size={vertical ? 16 : 15} className="shrink-0" />
        <span className={vertical ? "truncate" : ""}>More</span>
        {vertical
          ? <ChevronRight size={14} className="ml-auto opacity-70" />
          : <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed w-56 fl-card p-1.5 z-[200]"
          style={vertical
            ? { top: pos.top, left: pos.left, boxShadow: "var(--fl-shadow)" }
            : { top: pos.top, right: pos.right, boxShadow: "var(--fl-shadow)" }}
          data-testid={`${testidPrefix}-more-menu`}
        >
          {items.map((l) => {
            const Icon = l.icon;
            const active = l.to.split("?")[0] === activePath;
            const a = accentFor ? accentFor(l) : accent;
            return (
              <Link
                key={l.to}
                to={l.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                data-testid={`more-${l.testid}`}
                data-active={active ? "true" : "false"}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/5"
                style={{ color: active ? a : "var(--fl-text)" }}
              >
                {Icon && <Icon size={15} style={{ color: active ? a : "var(--fl-muted)" }} />}
                <span className="flex-1">{l.label}</span>
                {Number(l.badge || 0) > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold text-white" style={{ background: "var(--fl-orange)" }}>
                    {l.badge > 99 ? "99+" : l.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
